import React, { useMemo, useState } from 'react';
import type { ActiveStatus, TagOrigin, TagQuery, TagResponse } from '@troia-v3/types';

import {
  Button,
  FiltersButton,
  ListPageEmpty,
  ListPageLoading,
  ListPagePagination,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  PageToolbar,
  PlainHeadCell,
  SortableHeadCell,
  ToolbarSearch,
} from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { usePermissions } from '@/hooks/usePermissions';

import { TagFormDrawer } from '../components/TagFormDrawer';
import { useDeleteTag, useTags, useUpdateTag } from '../hooks/useTags';

type SortKey = 'name' | 'usageCount';

/**
 * TagsPage — o catálogo de tags da empresa (Configurações → Tags).
 *
 * É a tela que faltava: até 22/09/2026 não existia collection de tags, e a
 * lista de tags "disponíveis" era um `distinct` sobre os contatos. O efeito
 * prático era que **não havia como cadastrar uma tag sem marcar um contato** —
 * um gestor que quisesse preparar o vocabulário da equipe antes de começar a
 * operar não tinha por onde.
 *
 * Duas decisões de tela que vêm do dado, não do gosto:
 *
 * - **`withUsage` está sempre ligado.** A pergunta que traz alguém aqui é
 *   quase sempre "posso apagar esta?", e ela não se responde sem o número de
 *   quem está marcado. O custo é uma agregação sobre quatro collections, que
 *   se paga numa tela de baixo tráfego.
 * - **Busca e ordenação no SERVIDOR.** O maior tenant em produção tem 315
 *   tags; ordenar só a página carregada faria a página 2 recomeçar o alfabeto.
 */
export const TagsPage: React.FC = () => {
  const { hasModulePermission } = usePermissions();
  const podeCriar = hasModulePermission('tags', 'create');
  const podeEditar = hasModulePermission('tags', 'update');
  const podeExcluir = hasModulePermission('tags', 'delete');

  const [searchTerm, setSearchTerm] = useState('');
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'name', dir: 'asc' });
  const [query, setQuery] = useState<TagQuery>({
    page: 1,
    limit: 20,
    sortBy: 'name',
    sortOrder: 'asc',
    withUsage: true,
  });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<TagResponse | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TagResponse | null>(null);
  const [purgarAoExcluir, setPurgarAoExcluir] = useState(false);

  const { data, isLoading, error } = useTags(query);
  const updateTag = useUpdateTag();
  const deleteTag = useDeleteTag();

  const items = useMemo(() => data?.items ?? [], [data]);

  const setSearch = (term: string): void => {
    setSearchTerm(term);
    setQuery((prev) => ({ ...prev, search: term || undefined, page: 1 }));
  };

  const handleSort = (key: SortKey): void => {
    const dir = sort.key === key && sort.dir === 'asc' ? 'desc' : 'asc';
    setSort({ key, dir });
    setQuery((prev) => ({ ...prev, sortBy: key, sortOrder: dir, page: 1 }));
  };

  const aplicarFiltro = (patch: Partial<NonNullable<TagQuery['filters']>>): void => {
    setQuery((prev) => {
      const filters = { ...prev.filters, ...patch };
      // Chave com `undefined` continua existindo no objeto e viraria
      // `?filters[status]=` na URL — limpar de verdade é remover.
      for (const chave of Object.keys(filters) as Array<keyof typeof filters>) {
        if (filters[chave] === undefined) delete filters[chave];
      }
      return { ...prev, filters: Object.keys(filters).length > 0 ? filters : undefined, page: 1 };
    });
    setFiltersOpen(false);
  };

  const handleNew = (): void => {
    setEditing(null);
    setDrawerOpen(true);
  };

  const handleEdit = (tag: TagResponse): void => {
    setEditing(tag);
    setDrawerOpen(true);
  };

  const handleToggleStatus = (tag: TagResponse): void => {
    const proximo: ActiveStatus = tag.status === 'active' ? 'inactive' : 'active';
    updateTag.mutate({ id: tag.id, data: { status: proximo } });
  };

  const abrirExclusao = (tag: TagResponse): void => {
    setPurgarAoExcluir(false);
    setDeleteTarget(tag);
  };

  const confirmarExclusao = (): void => {
    if (deleteTarget) deleteTag.mutate({ id: deleteTarget.id, purge: purgarAoExcluir });
    setDeleteTarget(null);
  };

  const statusLabel =
    query.filters?.status === 'active' ? 'Ativas' : query.filters?.status === 'inactive' ? 'Inativas' : null;
  const originLabel =
    query.filters?.origin === 'auto' ? 'Cadastradas automaticamente' : query.filters?.origin === 'manual' ? 'Cadastradas na tela' : null;
  const activeFilterCount = [statusLabel, originLabel].filter(Boolean).length;

  const filterOptions = (
    <>
      <p className="px-2.5 pt-1.5 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>Status</p>
      {([[undefined, 'Todas'], ['active', 'Ativas'], ['inactive', 'Inativas']] as const).map(([val, label]) => {
        const ativo = query.filters?.status === val;
        return (
          <button
            key={`status-${label}`}
            onClick={() => aplicarFiltro({ status: val })}
            className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-left"
            style={{ color: '#374151', backgroundColor: ativo ? '#f3e8ff' : 'transparent' }}
            data-testid={`tags-filter-status-${label}`}
          >
            {label}
            {ativo && <Icon name="check" className="w-4 h-4" style={{ color: '#8b5cf6' }} />}
          </button>
        );
      })}

      <p className="px-2.5 pt-3 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>Origem</p>
      {/* "Cadastradas automaticamente" é o filtro que faz o auto-cadastro ser
          administrável: sem ele, a lista só cresce e ninguém revisa. */}
      {([[undefined, 'Todas'], ['manual', 'Cadastradas na tela'], ['auto', 'Cadastradas automaticamente']] as const).map(
        ([val, label]) => {
          const ativo = query.filters?.origin === val;
          return (
            <button
              key={`origin-${label}`}
              onClick={() => aplicarFiltro({ origin: val as TagOrigin | undefined })}
              className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-left"
              style={{ color: '#374151', backgroundColor: ativo ? '#f3e8ff' : 'transparent' }}
              data-testid={`tags-filter-origin-${label}`}
            >
              {label}
              {ativo && <Icon name="check" className="w-4 h-4" style={{ color: '#8b5cf6' }} />}
            </button>
          );
        },
      )}
    </>
  );

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      <PageToolbar
        left={
          <>
            <span className="font-bold text-xl tracking-tight truncate" style={{ color: 'var(--theme-text)' }}>Tags</span>
            {data?.total != null && (
              <span
                className="text-xs font-semibold rounded-full px-2 py-0.5 tabular-nums flex-none"
                style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
                data-testid="tags-count"
              >
                {data.total}
              </span>
            )}
          </>
        }
        center={
          <ToolbarSearch value={searchTerm} onChange={setSearch} placeholder="Buscar tag…" data-testid="tags-search" />
        }
        right={
          <>
            <div className="relative flex-none">
              <FiltersButton
                count={activeFilterCount}
                active={filtersOpen}
                onClick={() => setFiltersOpen((v) => !v)}
                data-testid="tags-filters"
              />
              {filtersOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setFiltersOpen(false)} />
                  <div
                    className="absolute right-0 top-full mt-2 z-50 w-64 rounded-xl p-2 max-h-[70vh] overflow-y-auto"
                    style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', boxShadow: '0 4px 24px rgba(0,0,0,.12)' }}
                    data-testid="tags-filters-popover"
                  >
                    {filterOptions}
                  </div>
                </>
              )}
            </div>

            {podeCriar && (
              <>
                <span className="hidden md:block w-px h-[22px] flex-none" style={{ backgroundColor: 'var(--theme-input-border)' }} />
                <Button variant="primary" icon="plus" collapsibleLabel onClick={handleNew} data-testid="new-tag">
                  Nova tag
                </Button>
              </>
            )}
          </>
        }
      />

      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6">
          {error && (
            <div
              className="rounded-lg p-4"
              style={{ backgroundColor: 'var(--theme-error-subtle)', border: '1px solid var(--theme-error-color)' }}
              data-testid="tags-error"
            >
              <p style={{ color: 'var(--theme-error-fg)' }}>Erro ao carregar tags: {error.message}</p>
            </div>
          )}

          {isLoading && <ListPageLoading message="Carregando tags..." />}

          {!isLoading && !error && items.length === 0 && (
            <ListPageEmpty
              icon="tag"
              message="Nenhuma tag encontrada"
              description={
                searchTerm || activeFilterCount > 0
                  ? 'Tente ajustar a busca ou os filtros.'
                  : 'Cadastre o vocabulário da sua equipe aqui. As tags ficam disponíveis no chat, no CRM, nos tickets e nas automações — sem precisar marcar ninguém antes.'
              }
              {...(podeCriar ? { actionLabel: 'Nova tag', onAction: handleNew } : {})}
            />
          )}

          {!isLoading && !error && items.length > 0 && (
            <div
              className="rounded-xl overflow-hidden"
              style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}
            >
              <table className="w-full border-collapse" data-testid="tags-table">
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                    <SortableHeadCell<SortKey> label="Tag" sortKey="name" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                    <PlainHeadCell label="Descrição" className="hidden lg:table-cell" />
                    <SortableHeadCell<SortKey> label="Em uso" sortKey="usageCount" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                    <PlainHeadCell label="Origem" className="hidden md:table-cell" />
                    <PlainHeadCell label="Status" />
                    <PlainHeadCell className="w-24" />
                  </tr>
                </thead>
                <tbody>
                  {items.map((tag) => (
                    <tr
                      key={tag.id}
                      style={{ borderBottom: '1px solid var(--theme-card-border)' }}
                      data-testid={`tags-row-${tag.name}`}
                    >
                      <td className="px-4 py-3">
                        <span
                          className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border"
                          style={{ backgroundColor: `${tag.color}1A`, color: tag.color, borderColor: `${tag.color}33` }}
                        >
                          {tag.name}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm hidden lg:table-cell" style={{ color: 'var(--theme-muted-text)' }}>
                        {tag.description ?? '—'}
                      </td>
                      <td className="px-4 py-3 text-sm tabular-nums" style={{ color: 'var(--theme-card-text)' }}>
                        {tag.usageCount ?? 0}
                      </td>
                      <td className="px-4 py-3 text-sm hidden md:table-cell" style={{ color: 'var(--theme-muted-text)' }}>
                        {tag.origin === 'auto' ? 'Automática' : 'Manual'}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold"
                          style={
                            tag.status === 'active'
                              ? { backgroundColor: '#dcfce7', color: '#15803d' }
                              : { backgroundColor: '#f1f5f9', color: '#64748b' }
                          }
                        >
                          {tag.status === 'active' ? 'Ativa' : 'Inativa'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {podeEditar && (
                            <>
                              <button
                                onClick={() => handleToggleStatus(tag)}
                                title={tag.status === 'active' ? 'Desativar' : 'Ativar'}
                                aria-label={tag.status === 'active' ? `Desativar ${tag.name}` : `Ativar ${tag.name}`}
                                className="p-1.5 rounded-lg hover:bg-black/5"
                                data-testid={`tags-toggle-${tag.name}`}
                              >
                                <Icon name="power" className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
                              </button>
                              <button
                                onClick={() => handleEdit(tag)}
                                title="Editar"
                                aria-label={`Editar ${tag.name}`}
                                className="p-1.5 rounded-lg hover:bg-black/5"
                                data-testid={`tags-edit-${tag.name}`}
                              >
                                <Icon name="edit" className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
                              </button>
                            </>
                          )}
                          {podeExcluir && (
                            <button
                              onClick={() => abrirExclusao(tag)}
                              title="Excluir"
                              aria-label={`Excluir ${tag.name}`}
                              className="p-1.5 rounded-lg hover:bg-black/5"
                              data-testid={`tags-delete-${tag.name}`}
                            >
                              <Icon name="trash" className="w-4 h-4" style={{ color: 'var(--theme-error-fg)' }} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!isLoading && !error && (data?.totalPages ?? 0) > 1 && (
            <ListPagePagination
              currentPage={data?.page ?? 1}
              totalPages={data?.totalPages ?? 1}
              onPageChange={(page) => setQuery((prev) => ({ ...prev, page }))}
            />
          )}
        </div>
      </div>

      <TagFormDrawer
        isOpen={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setEditing(null);
        }}
        tag={editing}
      />

      {/*
        Modal próprio, e não o `DeleteConfirmationModal` compartilhado, porque
        aqui há uma ESCOLHA a fazer, não só uma confirmação: tirar a tag do
        catálogo é reversível (basta recadastrar), mas apagar a marcação de
        quem já foi marcado não tem volta. O padrão é a opção reversível.
      */}
      {deleteTarget && (
        <Modal isOpen onClose={() => setDeleteTarget(null)}>
          <ModalHeader onClose={() => setDeleteTarget(null)}>Excluir tag</ModalHeader>
          <ModalContent>
            <p className="text-sm" style={{ color: '#1e293b' }}>
              Excluir a tag <strong>{deleteTarget.name}</strong> do catálogo?
            </p>
            <p className="text-xs mt-2" style={{ color: '#64748b' }}>
              Ela some do seletor e das automações. {(deleteTarget.usageCount ?? 0) > 0
                ? `Os ${deleteTarget.usageCount} registros que já têm esta tag continuam com ela e seguem aparecendo nos filtros.`
                : 'Nenhum registro usa esta tag hoje.'}
            </p>

            {(deleteTarget.usageCount ?? 0) > 0 && (
              <label
                className="flex items-start gap-2 mt-4 p-3 rounded-lg cursor-pointer"
                style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}
              >
                <input
                  type="checkbox"
                  checked={purgarAoExcluir}
                  onChange={(e) => setPurgarAoExcluir(e.target.checked)}
                  className="mt-0.5"
                  data-testid="tags-delete-purge"
                />
                <span className="text-xs" style={{ color: '#991b1b' }}>
                  Remover também dos {deleteTarget.usageCount} registros marcados.{' '}
                  <strong>Não tem volta</strong> — a marcação é histórico de quem atendeu.
                </span>
              </label>
            )}
          </ModalContent>
          <ModalFooter>
            <Button variant="secondary" onClick={() => setDeleteTarget(null)} data-testid="tags-delete-cancel">
              Cancelar
            </Button>
            <Button
              variant="error"
              onClick={confirmarExclusao}
              disabled={deleteTag.isPending}
              data-testid="tags-delete-confirm"
            >
              {deleteTag.isPending ? 'Excluindo...' : 'Excluir'}
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </div>
  );
};

export default TagsPage;
